import htmlToPlainText from './htmlToPlainText';

/**
 * Return an absolute HTTP(S) URL or null.
 *
 * Listing URLs can originate in employer feeds and third-party scrapers, so
 * they must not be placed in an href until the scheme has been allowlisted.
 */
export const safeListingUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;

  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};

export const getFullListingDescription = (listing = {}) =>
  htmlToPlainText(
    listing.fullDescription ||
      listing.description ||
      listing.briefDescription ||
      listing.summary,
  ) || 'No description available.';

/**
 * Keep feed prose out of the modal heading. Several external transforms use
 * `briefDescription` as a legacy title fallback, so a long posting could be
 * rendered once as the heading and then repeated as the body.
 */
export const getListingDetailsTitle = (listing = {}) => {
  const providerType = htmlToPlainText(
    listing.providerType || listing.section,
  );
  const fallback = providerType
    ? `${providerType} position`
    : 'Listing details';
  const title = htmlToPlainText(
    listing.positionTitle || listing.roleTitle || listing.title,
  );
  if (!title || title.length > 120) return fallback;

  const body = htmlToPlainText(
    listing.fullDescription ||
      listing.description ||
      listing.summary ||
      listing.briefDescription,
  );
  return title === body ? fallback : title;
};

/**
 * Normalize all listing link dialects into the two links a provider understands.
 * Source/apply links are preferred over facility websites and duplicate URLs
 * are collapsed so a feed that copied its apply URL into `website` shows once.
 */
export const getListingLinks = (listing = {}) => {
  const original = safeListingUrl(
    listing.sourceUrl ||
      listing.originalListingUrl ||
      listing.originalUrl ||
      listing.listingUrl ||
      listing.url ||
      listing.applyUrl ||
      listing.inquireLink,
  );
  const website = safeListingUrl(listing.website);
  const links = [];

  if (original) {
    links.push({
      key: 'original',
      label: 'View original listing',
      url: original,
    });
  }

  if (website && website !== original) {
    links.push({
      key: 'website',
      label: 'Visit requisition website',
      url: website,
    });
  }

  return links;
};
